import { Typography } from 'antd'
import { CSSProperties, ReactNode } from 'react'
import { HIGHLIGHT_COLOR, useFieldHighlight } from './highlight'
import { FieldHighlightProps, WalletField } from './types'
import WalletFieldTags, { WalletFieldTag } from './WalletFieldTags'

const { Text } = Typography

interface Props extends FieldHighlightProps {
  field: WalletField
  children: ReactNode
  /**
   * Which pass surfaces the field appears on and whether it is required there,
   * shown as small chips instead of prose.
   */
  tags?: WalletFieldTag[]
  /** Short note on *where* the value lands on the pass. */
  hint?: ReactNode
  style?: CSSProperties
}

/**
 * Wraps a form control and ties it to the preview: focusing or hovering it
 * marks the field active (its preview region lights up), and it highlights
 * itself when the field is active from the other side — hovering the matching
 * region in the preview. `tags` chip the surfaces/requirement, `hint` says
 * where the value appears.
 */
const WalletFormField = ({
  field,
  activeField,
  onActiveFieldChange,
  tags,
  hint,
  style,
  children,
}: Props) => {
  const { isActive, handlers } = useFieldHighlight(field, {
    activeField,
    onActiveFieldChange,
  })

  return (
    <div
      {...handlers}
      style={{
        padding: '8px 12px',
        marginBottom: 4,
        borderRadius: 8,
        boxShadow: isActive ? `inset 3px 0 0 ${HIGHLIGHT_COLOR}` : 'none',
        background: isActive ? 'rgba(22, 119, 255, 0.06)' : 'transparent',
        transition: 'background 120ms ease, box-shadow 120ms ease',
        ...style,
      }}
    >
      {children}
      {(tags?.length || hint) && (
        // Stacked (tags over hint), never side by side, so a short hint doesn't
        // float to the right of the chips on some fields and wrap under on
        // others. Positive top gap so the chips clear the control.
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 6,
            marginTop: 8,
          }}
        >
          {tags?.length ? <WalletFieldTags tags={tags} /> : null}
          {hint && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {hint}
            </Text>
          )}
        </div>
      )}
    </div>
  )
}

export default WalletFormField
